// src/pages/ProductDetailPage.jsx
import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchProductOptions, fetchPrice, fetchProducts } from "../services/sinaliteService";
import { doc, getDoc } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import { useCart } from "../context/CartContext";
import API_BASE from "../services/apiClient";
import "./ProductDetailPage.css";

const PrinterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);
const TruckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
    <circle cx="7" cy="21" r="2" /><path d="M9 21h6" /><circle cx="17" cy="21" r="2" />
    <path d="M17 3v5h2a2 2 0 0 1 2 2v7" />
  </svg>
);
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" />
  </svg>
);

// ── Image placeholder ─────────────────────────────────────────────────────────
function ImageGallery({ images = [] }) {
  const [active, setActive] = useState(0);
  if (images.length === 0) {
    return <div className="pd-img-placeholder"><PrinterIcon /></div>;
  }
  return (
    <div className="pd-gallery anim-fade-in">
      <div className="pd-gallery-main img-zoom">
        <img src={images[active]} alt="product" />
        {images.length > 1 && (
          <>
            <button className="pd-gallery-arrow left"
              onClick={() => setActive(i => (i - 1 + images.length) % images.length)}>‹</button>
            <button className="pd-gallery-arrow right"
              onClick={() => setActive(i => (i + 1) % images.length)}>›</button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="pd-gallery-thumbs">
          {images.map((url, i) => (
            <button key={i} className={`pd-thumb ${i === active ? "active" : ""}`} onClick={() => setActive(i)}>
              <img src={url} alt={`thumb-${i}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const navigate = useNavigate();

  const [grouped, setGrouped] = useState({});   // { "Stock": [...], "size": [...], "qty": [...] }
  const [selected, setSelected] = useState({});  // { "Stock": 30, "size": 4, "qty": 105 }
  const [loading, setLoading] = useState(true);
  const [priceLoading, setPriceLoading] = useState(false);
  const [displayPrice, setDisplayPrice] = useState(null);
  const [sinalitePrice, setSinalitePrice] = useState(null);
  const [optionLabels, setOptionLabels] = useState({});
  const [addedToCart, setAddedToCart] = useState(false);
  const [error, setError] = useState("");

  // Product meta (name, category, image, description)
  const [productMeta, setProductMeta] = useState(null);

  // Description of the product's parent category (shown under the category name)
  const [categoryDescription, setCategoryDescription] = useState("");

  // Load product meta + options on mount
  useEffect(() => {
    setLoading(true);

    // Load product name/category/image/description
    // Load both in parallel then merge — saved metadata wins for imageUrl/description
    Promise.all([
      fetch(`${API_BASE}/supplier/proxy?path=/product`)
        .then(r => r.json())
        .then(products => products.find(p => String(p.id) === String(id)) || null)
        .catch(() => null),
      getDoc(doc(db, "productMeta", String(id)))
        .then(snap => snap.exists() ? snap.data() : null)
        .catch(() => null),
    ]).then(([sinaliteProduct, productMeta]) => {
      const imageUrl = productMeta?.imageUrl || "";
      const images   = productMeta?.images?.length
        ? productMeta.images
        : imageUrl ? [imageUrl] : [];
      setProductMeta({
        ...(sinaliteProduct || {}),
        imageUrl,
        images,
        description: productMeta?.description || sinaliteProduct?.description || "",
      });

      // Look up the parent category's own description (separate from the product's description)
      const categoryName = sinaliteProduct?.category || "";
      if (categoryName) {
        fetchProducts()
          .then(({ categories }) => {
            const cat = categories.find(c => c.name === categoryName);
            setCategoryDescription(cat?.description || "");
          })
          .catch(() => setCategoryDescription(""));
      }
    });

    // Load options
    fetchProductOptions(id)
      .then(({ grouped }) => {
        setGrouped(grouped);
        const defaults = {};
        for (const [group, opts] of Object.entries(grouped)) {
          if (opts.length > 0) defaults[group] = opts[0].id;
        }
        setSelected(defaults);
      })
      .catch(() => setError("Failed to load product options."))
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch price whenever selections change
  const fetchLivePrice = useCallback(async () => {
    const selectedIds = Object.values(selected);
    if (selectedIds.length === 0) return;
    setPriceLoading(true);
    try {
      const { displayPrice, sinalitePrice, productOptions } = await fetchPrice(id, selectedIds, productMeta?.category);
      setDisplayPrice(displayPrice);
      setSinalitePrice(sinalitePrice);
      // Build labels map from productOptions response e.g. { "Stock": "14pt Cardstock", ... }
      setOptionLabels(productOptions);
    } catch {
      // silently fail — price will retry on next selection change
    } finally {
      setPriceLoading(false);
    }
  }, [id, selected, productMeta?.category]);

  useEffect(() => {
    if (Object.keys(selected).length > 0) {
      fetchLivePrice();
    }
  }, [fetchLivePrice]);

  const handleSelect = (group, optionId) => {
    setSelected(prev => ({ ...prev, [group]: optionId }));
  };

  const buildCartOptions = () => ({
    selectedIds: Object.values(selected),
    optionMap: Object.fromEntries(
      Object.entries(selected).map(([group, id]) => [group, String(id)])
    ),
    optionLabels,
    displayPrice: displayPrice || 0,
    sinalitePrice: sinalitePrice || 0,
    description: productMeta?.description || "",
  });

  const handleAddToCart = () => {
    if (!displayPrice) return;
    addItem(
      { id, name: productMeta?.name || `Product ${id}`, category: productMeta?.category || "", imageUrl: productMeta?.imageUrl || "", description: productMeta?.description || "" },
      buildCartOptions()
    );
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const handleAddToCartAndCheckout = () => {
    if (!displayPrice) return;
    addItem(
      { id, name: productMeta?.name || `Product ${id}`, category: productMeta?.category || "", imageUrl: productMeta?.imageUrl || "", description: productMeta?.description || "" },
      buildCartOptions()
    );
    navigate("/cart");
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center" }}>Loading product...</div>;
  if (error) return <div style={{ padding: 60, textAlign: "center" }}>{error} <Link to="/products">Back</Link></div>;

  return (
    <div className="pd-page">
      <div className="container pd-layout">
        {/* Left */}
        <div className="pd-info anim-fade-in-up">
          <ImageGallery images={productMeta?.images?.length ? productMeta.images : (productMeta?.imageUrl ? [productMeta.imageUrl] : [])} />
          <span className="tag" style={{ marginTop: 20, display: "block", fontSize: 16 }}>
            {productMeta?.category || ""}
          </span>
          {categoryDescription && (
            <p className="pd-category-description" style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.6, marginTop: -6, marginBottom: 10 }}>
              {categoryDescription}
            </p>
          )}
          <h1 className="pd-title">{productMeta?.name || `Product #${id}`}</h1>
          {productMeta?.description && (
            <p className="pd-description" style={{ color: "var(--text-muted)", fontSize: 15, lineHeight: 1.7, marginTop: 8 }}>
              {productMeta.description}
            </p>
          )}
          {displayPrice && (
            <p className="pd-price">
              From <strong>${displayPrice.toFixed(2)}</strong>
            </p>
          )}
          <div className="pd-trust">
            <div><TruckIcon /> Fast shipping via UPS</div>
            <div><CheckIcon /> Quality guaranteed</div>
          </div>

          {/* ── FAQs ── */}
          <div className="pd-faq-section" style={{ marginTop: 36 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 16, color: "var(--text)" }}>Frequently Asked Questions</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {PRODUCT_FAQS.map((faq, i) => (
                <FaqItem key={i} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </div>

          {/* ── Guides ── */}
          <div className="pd-guides-section" style={{ marginTop: 32 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 16, color: "var(--text)" }}>Helpful Guides</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {PRODUCT_GUIDES.map((guide, i) => (
                <a key={i} href={guide.href} target="_blank" rel="noopener noreferrer" className="pd-guide-card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, background: "var(--surface, #fff)", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, textDecoration: "none" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 8, background: "rgba(0,170,221,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "var(--cyan)" }}>
                    <GuideIcon />
                  </div>
                  <div>
                    <strong style={{ display: "block", fontSize: 13, color: "var(--text)" }}>{guide.title}</strong>
                    <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{guide.desc}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Order Form */}
        <div className="pd-order-box anim-fade-in-up" style={{ animationDelay: "0.12s" }}>
          <div className="pd-order-header">
            <h2>Customize Your Order</h2>
            <p>Select your options to see the price.</p>
          </div>

          <div className="order-form">
            {Object.entries(grouped).map(([group, opts]) => (
              <div className="form-group" key={group}>
                <label>{group}</label>
                {group === "qty" ? (
                  // Quantity → button grid
                  <div className="qty-grid">
                    {opts.map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        className={`qty-btn ${selected[group] === opt.id ? "active" : ""}`}
                        onClick={() => handleSelect(group, opt.id)}
                      >
                        {opt.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  // Other options → dropdown
                  <select
                    value={selected[group] || ""}
                    onChange={e => handleSelect(group, parseInt(e.target.value))}
                  >
                    {opts.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}

            {/* Live Price */}
            <div className="pd-live-price">
              <span>Your Price</span>
              {priceLoading ? (
                <strong>Calculating...</strong>
              ) : displayPrice ? (
                <strong>${displayPrice.toFixed(2)}</strong>
              ) : (
                <strong>—</strong>
              )}
            </div>

            <div className="cart-action-btns">
              <button
                type="button"
                className="btn-primary order-submit"
                onClick={handleAddToCartAndCheckout}
                disabled={!displayPrice || priceLoading}
              >
                Add to Cart & Checkout
              </button>
              <button
                type="button"
                className="btn-outline order-submit-outline"
                onClick={handleAddToCart}
                disabled={!displayPrice || priceLoading}
              >
                {addedToCart ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <CheckIcon /> Added!
                  </span>
                ) : "+ Add to Cart"}
              </button>
            </div>
            <p className="form-footnote">You'll upload your artwork file at checkout.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Static FAQ + Guides content (applies to all products) ──────────────────
const PRODUCT_FAQS = [
  {
    q: "What file format should I upload for printing?",
    a: "We accept print-ready PDF files for best results. AI, EPS, PSD, PNG and JPG are also supported. Your file should be set up in CMYK colour mode at 300 DPI resolution with all fonts outlined or embedded.",
  },
  {
    q: "Do I need to include bleed in my design?",
    a: "Yes. Please add 1/8 inch (0.125 in) of bleed on all sides of your artwork, and keep important text or logos at least 1/8 inch away from the trim edge to avoid them being cut off.",
  },
  {
    q: "How long does production take before shipping?",
    a: "Production time depends on the turnaround option you select at checkout — ranging from next business day to standard production. Shipping time is added on top of production time and varies by destination.",
  },
  {
    q: "Can I get a sample before placing a full order?",
    a: "For most products you can order a smaller quantity first to check quality and colour accuracy before committing to a larger print run. Contact us if you'd like guidance on sample sizing.",
  },
  {
    q: "What if my artwork has an issue after I submit it?",
    a: "Our team reviews submitted artwork before it goes into production. If there's a problem with bleed, resolution, or file format, we'll reach out to you by email before printing begins.",
  },
];

const PRODUCT_GUIDES = [
  { title: "Artwork Setup Guide",        desc: "Bleeds, margins & file specs",       href: "/artwork-guidelines" },
  { title: "Colour & CMYK Guide",        desc: "Getting accurate print colour",      href: "/artwork-guidelines" },
  { title: "File Format Requirements",   desc: "PDF only — 300 DPI, CMYK required",  href: "/artwork-guidelines" },
  { title: "Purchasing Guide",           desc: "How to order, step by step",         href: "/purchasing-guide" },
  { title: "Shipping & Turnaround Info", desc: "Production & delivery times",        href: "/faq" },
];

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, overflow: "hidden", background: "var(--surface, #fff)" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
      >
        <span style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>{question}</span>
        <span style={{ color: "var(--cyan)", fontSize: 18, transform: open ? "rotate(45deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0, marginLeft: 12 }}>+</span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 16px", fontSize: 13.5, lineHeight: 1.7, color: "var(--text-muted)" }}>
          {answer}
        </div>
      )}
    </div>
  );
}

function GuideIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
